import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const elementIds = new WeakMap();
let nextElementId = 1;

function elementId(element) {
  if (!element) return "none";
  if (!elementIds.has(element)) {
    elementIds.set(element, nextElementId);
    nextElementId += 1;
  }
  return String(elementIds.get(element));
}

function sectionTitle(section, index) {
  const title = section?.querySelector?.(":scope > h3, :scope .window_help_section_title")?.textContent;
  return String(title || `Sezione ${index + 1}`).trim();
}

function sectionHasError(section) {
  if (!section) return false;
  return Boolean(section.querySelector([
    ".overlay_validation_invalid",
    ".overlay_field.is_invalid",
    ".overlay_field_validation[role=\"alert\"]",
    "[aria-invalid=\"true\"]",
  ].join(",")));
}

function sectionHasSearchMatch(section) {
  return Boolean(section?.classList?.contains("is_search_match") || section?.dataset?.overlaySearchMatch === "true");
}

function topmostOverlayCard() {
  const cards = [...document.querySelectorAll(".overlay_backdrop .overlay_card")];
  return cards[cards.length - 1] || null;
}

function collectSections(card) {
  if (!card) return [];
  const body = card.querySelector(":scope > .overlay_body");
  if (!body) return [];
  const sections = [...body.querySelectorAll(".overlay_section, .window_help_section")]
    .filter((section, index, list) => list.indexOf(section) === index)
    .filter((section) => section.closest(".overlay_card") === card)
    .filter((section) => section.offsetParent !== null || section.classList.contains("window_help_section"));
  return sections.map((element, index) => ({
    element,
    title: sectionTitle(element, index),
    hasError: sectionHasError(element),
    hasSearchMatch: sectionHasSearchMatch(element),
  }));
}

function signatureFor(card, sections) {
  return [
    elementId(card),
    ...sections.map((section, index) => `${index}:${elementId(section.element)}:${section.title}:${section.hasError ? 1 : 0}:${section.hasSearchMatch ? 1 : 0}`),
  ].join("|");
}

export default function OverlaySectionNavigator() {
  const [card, setCard] = useState(null);
  const [sections, setSections] = useState([]);
  const signatureRef = useRef("");

  useEffect(() => {
    let frame = 0;

    const sync = () => {
      frame = 0;
      const nextCard = topmostOverlayCard();
      const nextSections = collectSections(nextCard);
      const nextSignature = signatureFor(nextCard, nextSections);
      if (nextSignature === signatureRef.current) return;
      signatureRef.current = nextSignature;
      setCard((current) => (current === nextCard ? current : nextCard));
      setSections(nextSections);
    };

    const scheduleSync = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(sync);
    };

    sync();
    const observer = new MutationObserver(scheduleSync);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class", "aria-invalid", "data-overlay-search-match"],
    });

    window.addEventListener("resize", scheduleSync);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", scheduleSync);
    };
  }, []);

  useEffect(() => {
    const cards = [...document.querySelectorAll(".overlay_backdrop .overlay_card")];
    cards.forEach((item) => item.classList.toggle("overlay_has_section_nav", item === card && sections.length > 0));
    return () => card?.classList?.remove("overlay_has_section_nav");
  }, [card, sections.length]);

  if (!card || sections.length === 0) return null;

  function goToSection(section) {
    const body = card.querySelector(":scope > .overlay_body");
    if (!body || !section?.element) return;
    const bodyRect = body.getBoundingClientRect();
    const sectionRect = section.element.getBoundingClientRect();
    const bodyPaddingTop = Number.parseFloat(window.getComputedStyle(body).paddingTop || "0") || 0;
    const top = Math.max(0, body.scrollTop + sectionRect.top - bodyRect.top - bodyPaddingTop);
    body.scrollTo({ top, behavior: "smooth" });
  }

  return createPortal(
    <nav className="overlay_section_nav" aria-label="Navigazione sezioni overlay">
      {sections.map((section, index) => (
        <button
          key={`${section.title}-${index}`}
          type="button"
          className={`overlay_section_nav_dot${section.hasError ? " is_error" : ""}${section.hasSearchMatch ? " is_search_match" : ""}`}
          onClick={() => goToSection(section)}
          aria-label={`Vai a ${section.title}`}
          title={section.title}
          tabIndex={-1}
        >
          <span aria-hidden="true" />
        </button>
      ))}
    </nav>,
    card,
  );
}
